<script lang="ts">
  import type { Component } from 'svelte';
  import type { HTMLButtonAttributes } from 'svelte/elements';

  interface Props extends Omit<HTMLButtonAttributes, 'children'> {
    /** Accessible name, also shown as a tooltip. */
    label: string;
    icon: Component<{ size?: number; 'aria-hidden'?: boolean | 'true' }>;
    /** Filled/pressed look, e.g. a comment that exists. */
    active?: boolean;
    tooltip?: 'top' | 'bottom' | 'none';
    /** Tooltip text when it should differ from the label (e.g. a comment preview). */
    tooltipText?: string;
    /** Extra description for assistive tech (`aria-describedby`). */
    description?: string;
    /** Horizontal tooltip alignment; use `end` near the right edge of the screen. */
    tooltipAlign?: 'center' | 'start' | 'end';
    size?: 'sm' | 'md';
  }

  let {
    label,
    icon: Icon,
    active = false,
    tooltip = 'top',
    tooltipAlign = 'center',
    tooltipText,
    description,
    size = 'md',
    type = 'button',
    class: className,
    ...rest
  }: Props = $props();

  // Escape hides the tooltip until the pointer or focus comes back (WCAG 1.4.13).
  let suppressed = $state(false);
  const id = $props.id();
  function onWindowKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') suppressed = true;
  }
</script>

<svelte:window onkeydown={onWindowKeydown} />

<span class="relative inline-flex">
  <button
    {type}
    aria-label={label}
    aria-describedby={description ? `${id}-description` : undefined}
    class={[
      'peer inline-flex items-center justify-center rounded-lg text-ink-muted transition-colors duration-150 hover:bg-surface-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50',
      size === 'sm' ? 'size-8' : 'size-10',
      'pointer-coarse:size-11',
      active && 'text-accent',
      className,
    ]}
    onfocus={() => (suppressed = false)}
    onpointerenter={() => (suppressed = false)}
    {...rest}
  >
    <Icon size={size === 'sm' ? 16 : 20} aria-hidden="true" />
  </button>
  {#if description}
    <span id="{id}-description" class="sr-only">{description}</span>
  {/if}
  {#if tooltip !== 'none'}
    <span
      aria-hidden="true"
      class={[
        'pointer-events-none absolute z-50 rounded-md bg-ink px-2 py-1 text-xs font-medium text-canvas opacity-0 transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100 peer-aria-expanded:hidden',
        tooltip === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
        tooltipText === undefined ? 'whitespace-nowrap' : 'w-max max-w-64 break-words',
        {
          center: 'left-1/2 -translate-x-1/2',
          start: 'left-0',
          end: 'right-0',
        }[tooltipAlign],
        suppressed && 'invisible',
      ]}
    >
      {tooltipText ?? label}
    </span>
  {/if}
</span>
